import { Button, Dropdown, Modal, Table } from "react-bootstrap"
import { Brand } from "../../App"
import { useEffect, useRef, useState } from "react"
import PublishBrandItem, { PublishBrandItemRef } from "../publish-brand-item/publish-brand-item"

import "./publish-brand.css"

interface Props {
  brandCode: string
  brands: Brand[]
  show: boolean
  runtimeEnv: string
  onHide?: (() => void) | undefined
}

interface hotelState {
  hotelReferenceId: string
  diffDone: boolean
  willPublish: boolean
  havePublished: boolean
}

interface counts {
  total: number
  diffed: number
  willPublish: number
  published: number
}

const StatusDiff = 0
const StatusPublish = 1
const StatusDone = 2

export default function PublishBrand(props: Props) {

  const [environment, setEnvironment] = useState(props.runtimeEnv)
  const [hotelStates, setHotelStates] = useState<hotelState[]>([])
  const [hotels, setHotels] = useState<string[]>([])

  const [status, setStatus] = useState(StatusDiff)
  const [inProgress, setInProgress] = useState(false)
  const [trigger, setTrigger] = useState(false)

  const [counts, setCounts] = useState<counts>({ total: 0, diffed: 0, willPublish: 0, published: 0 })

  const refs = useRef<Array<PublishBrandItemRef | null>>([])

  useEffect(() => {
    // Get hotel ref IDs from brands collection, build initial diff array
    if (props.show) {
      const hotelList = props
        .brands
        .find((br) => br.brand_code === props.brandCode)?.hotels
        .map((hotel) => { return hotel.hotel_reference_id }) ?? []

      setHotels(hotelList)
      setHotelStates(hotelList.map((hotel) => {
        return { hotelReferenceId: hotel, diffDone: false, willPublish: false, havePublished: false }
      }))

      // Reset counts if we change environment
      setCounts({ total: hotelList.length, diffed: 0, willPublish: 0, published: 0 })
      setInProgress(false)
      setStatus(StatusDiff)
    } else {
      // Reset on hide
      setCounts({ total: 0, diffed: 0, willPublish: 0, published: 0 })
      setInProgress(false)
      setStatus(StatusDiff)
    }
  }, [props, environment])

  useEffect(() => {
    setEnvironment(props.runtimeEnv)
  }, [props.runtimeEnv])

  // Callback from publish-brand-item after hotel status change.
  // willPublish is true if a hotel will be published, or was just published, depending on the current status & inProgress flag
  function hotelProcessed(hotelReferenceId: string, isDiff: boolean, willPublish: boolean) {
    // Update the hotel state based on the supplied params
    const h = hotelStates
    const idx = h.findIndex((hotel) => hotel.hotelReferenceId === hotelReferenceId)

    if (isDiff) {
      h[idx].diffDone = true
      h[idx].willPublish = willPublish
    } else {
      h[idx].havePublished = willPublish
    }

    setCounts({
      total: h.length,
      diffed: h.filter((hh) => hh.diffDone).length,
      willPublish: h.filter((hh) => hh.willPublish).length,
      published: h.filter((hh) => hh.havePublished).length
    })
    setHotelStates(h)

    // Are we done getting diff results?
    if (status === StatusDiff && h.filter((hh) => !hh.diffDone).length === 0) {
      // Diff done, move to next stage
      setStatus(StatusPublish)
      setInProgress(false)
    }

    // Are we done publishing?
    if (status === StatusPublish && h.filter((hh) => hh.willPublish && !hh.havePublished).length === 0) {
      // Publish done, move to next stage
      setStatus(StatusDone)
      setInProgress(false)
    }

    // User can still tick/untick any hotels that they didn't publish last time...
    // If they do this, change the status back to Publish
    if (status === StatusDone && h.filter((hh) => hh.willPublish && !hh.havePublished).length > 0) {
      // Another one to add to the list maybe?
      setStatus(StatusPublish)
    }
  }

  useEffect(() => {
    // Take appropriate action when progress increments
    if (trigger) {
      setTrigger(false)
      switch (status) {
        case StatusDiff:
          setInProgress(true)
          hotelStates.forEach((hotel, idx) => {
            refs.current[idx]?.diff()
          })
          break
        case StatusPublish:
          // If we click Publish, with no hotels to publish, it actually means "done"...
          if (counts.willPublish === 0) {
            if (props.onHide)
              props.onHide()
          }
          else {
            setInProgress(true)
            hotelStates.forEach((hotel, idx) => {
              refs.current[idx]?.publish()
            })
          }
          break
        case StatusDone:
          if (props.onHide)
            props.onHide()
          break
      }
    }
  }, [trigger, status, props, hotelStates, counts.willPublish])

  useEffect(() => {
    // Reset if we change environment
    hotelStates.forEach((hotel, idx) => {
      refs.current[idx]?.reset()
    })

  }, [environment, hotelStates])

  function proceed() {
    setTrigger(true)
  }


  return <>
    <Modal
      show={props.show}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>
          <div>Publish all {props.brandCode} hotels</div>
          <div style={{ fontSize: "0.6em" }}>...to the
            <Dropdown as="span" style={{ marginLeft: 5, marginRight: 5 }} onSelect={(eventkey) => setEnvironment(eventkey ?? "")}>
              <Dropdown.Toggle variant="secondary" id="choose-environment" disabled={inProgress}>{environment}</Dropdown.Toggle>
              <Dropdown.Menu>
                {props.runtimeEnv === "localhost"
                  ? <Dropdown.Item eventKey="localhost">localhost</Dropdown.Item>
                  : null
                }
                <Dropdown.Item eventKey="dev">dev</Dropdown.Item>
                <Dropdown.Item eventKey="staging">staging</Dropdown.Item>
                <Dropdown.Item eventKey="live">live</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            environment cache</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderHotelTable()}
      </Modal.Body>
      <Modal.Footer>
        {
          status === StatusDiff
            ? <div>Progress: {counts.diffed} / {counts.total} processed, {counts.willPublish} to publish</div>
            : <div>Progress: {counts.total} processed, {counts.published} / {counts.willPublish} published</div>
        }
        {
          status !== StatusDone && !(status === StatusPublish && counts.willPublish === 0)
            ? <Button variant="secondary" onClick={props.onHide} disabled={inProgress}>Cancel</Button>
            : null
        }

        <Button onClick={() => proceed()} disabled={inProgress}>{progressButtonLabel()}</Button>
      </Modal.Footer>
    </Modal>
  </>

  function renderHotelTable() {

    return <div className="pub-div">
      <Table bordered>
        <thead style={{ position: "sticky", top: -1, fontWeight: "bold" }}>
          <tr>
            <th style={{ width: "60%" }}>Hotel</th>
            <th style={{ width: "30%" }}>Status</th>
            <th>Publish</th>
          </tr>
        </thead>
        <tbody>
          {
            hotels.map((hotel, idx) => (
              <PublishBrandItem
                key={hotel}
                ref={(el) => refs.current[idx] = el}
                hotelReferenceId={hotel}
                environment={environment}
                hotelProcessed={hotelProcessed}
              />
            ))
          }
        </tbody>
      </Table>
    </div>
  }
  function progressButtonLabel(): JSX.Element {

    if (status === StatusDone || (status === StatusPublish && counts.willPublish === 0))
      return <>Done</>

    if (status === StatusDiff && !inProgress)
      return <>Start</>;

    if (status === StatusDiff)
      return <><img src={process.env.PUBLIC_URL + "/loading-spin.svg"} alt="diff spinner" /> Diffing...</>

    if (inProgress)
      return <><img src={process.env.PUBLIC_URL + "/loading-spin.svg"} alt="publish spinner" /> Publishing...</>

    return <>Publish</>

  }
}